import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactDOM from 'react-dom';
import { FaPrint } from 'react-icons/fa';
import { getProgressBillDetails } from '../../../../redux/Slices/ProgressBillSlice';
import { getContractsDetails } from '../../../../redux/Slices/ContractSlice';
import axios from 'axios';
import { BASE_URL, getToken, getCurrentLanguage, getCurrentExecutionQty, getPreviouslyExecutedQty } from '../../../../shared/utils.js/utils';
import { useTranslation } from 'react-i18next';

const getPreviouslyExecutedQtyFromItem = getPreviouslyExecutedQty;
const getCurrentQtyFromItem = getCurrentExecutionQty;

const ViewProgressBill = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const [bill, setBill] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [projectName, setProjectName] = useState('—');
  const [journalEntriesModal, setJournalEntriesModal] = useState({ open: false, entry: null });
  const [contractModal, setContractModal] = useState({ open: false, contract: null });

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setIsLoading(true);
        const payload = await dispatch(getProgressBillDetails({ id, language: i18n.language })).unwrap();
        const details = payload?.data || payload;
        setBill(details);

        if (details.project_id && !details.project) {
          try {
            const token = getToken();
            const currentLang = getCurrentLanguage();
            if (token) {
              const projectResponse = await axios.get(`${BASE_URL}/projects/${details.project_id}`, {
                headers: { Authorization: `Bearer ${token}`, 'Accept-Language': currentLang },
              });
              const projectData = projectResponse.data?.data || projectResponse.data;
              if (projectData) setProjectName(projectData.name_ar || projectData.name_en || '—');
            }
          } catch (projectError) {
            console.error('Failed to fetch project:', projectError);
          }
        } else if (details.project) {
          setProjectName(details.project.name_ar || details.project.name_en || '—');
        }
      } catch (error) {
        const errorMessage = typeof error === 'string'
          ? error
          : error?.message || error?.response?.data?.message || t('progressBills.errors.fetchError');
        toast.error(errorMessage, { rtl: true });
        navigate('/progress-bills');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [dispatch, id, navigate]);

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch { return value; }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '0.00';
    return parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtQty = (n) => {
    const num = parseFloat(n) || 0;
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
  };

  const getStatusLabel = (status) => {
    const map = {
      draft: t('progressBills.status.draft'),
      submitted: t('progressBills.status.submitted'),
      approved: t('progressBills.status.approved'),
      rejected: t('progressBills.status.rejected'),
      paid: t('progressBills.status.paid'),
    };
    return map[status] || status;
  };

  const escapeHtml = (str) => {
    if (!str) return '—';
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };

  // Patch invoice_summary: inject advance_payment_deduction row if missing from array,
  // then recalculate net_amount correctly.
  const patchSummary = (rawSummary, billData) => {
    let summary = [...rawSummary];
    const advanceVal = parseFloat(billData?.advance_payment_deduction) || 0;
    const hasAdvance = summary.some((r) => r.key === 'advance_payment_deduction');

    if (!hasAdvance && advanceVal > 0) {
      const grossIdx = summary.findIndex((r) => r.key === 'gross_amount');
      const insertIdx = grossIdx >= 0 ? grossIdx + 1 : Math.max(0, summary.length - 1);
      summary = [
        ...summary.slice(0, insertIdx),
        { key: 'advance_payment_deduction', label: 'خصم الدفعة المقدمة', value: advanceVal },
        ...summary.slice(insertIdx),
      ];
    }

    // Recalculate net_amount: gross - advance - previous_payments
    const netIdx = summary.findIndex((r) => r.key === 'net_amount');
    if (netIdx >= 0) {
      const grossAmt  = parseFloat(summary.find((r) => r.key === 'gross_amount')?.value)  || 0;
      const advance   = parseFloat(summary.find((r) => r.key === 'advance_payment_deduction')?.value) || 0;
      const prevPay   = parseFloat(summary.find((r) => r.key === 'previous_payments')?.value) || 0;
      const correctNet = grossAmt - advance - prevPay;
      summary = summary.map((r, i) => (i === netIdx ? { ...r, value: correctNet } : r));
    }

    return summary;
  };

  const handlePrint = async () => {
    if (!bill) return;

    const fetchB64 = (path) => fetch(path)
      .then(r => r.blob())
      .then(b => new Promise((res) => { const rd = new FileReader(); rd.onloadend = () => res(rd.result); rd.onerror = () => res(''); rd.readAsDataURL(b); }))
      .catch(() => '');

    const [headerImgB64, watermarkB64] = await Promise.all([
      fetchB64(`${publicUrl}/HeaderAce.png`),
      fetchB64(`${publicUrl}/ACE.png`),
    ]);

    const summary = patchSummary(
      Array.isArray(bill.invoice_summary) ? bill.invoice_summary : [],
      bill
    );
    // Determine running vs final: prefer explicit bill_category, then check invoice_summary,
    // then fall back based on type ('claim' without previous_payments = running-like but treat as non-running)
    const isRunning = bill.bill_category
      ? (bill.bill_category === 'running')
      : summary.length > 0
        ? !summary.some((r) => r.key === 'previous_payments')
        : bill.type !== 'bill'; // claim without summary = running

    // Compute netVal here so it can be used in the HTML template string below
    const netRow = summary.find((r) => r.key === 'net_amount');
    const netVal = netRow ? parseFloat(netRow.value) || 0 : parseFloat(bill.net_amount) || 0;

    const billTypeText   = bill.bill_category_label || (isRunning ? 'جاري' : 'ختامي');
    const projectNameTxt = escapeHtml(bill.project?.name_ar || bill.project?.name_en || projectName);
    const customerTxt    = escapeHtml(bill.project?.customer?.name_ar || bill.project?.customer?.name || '—');
    const billSeqNo      = (bill.bill_no || '').replace(/^.*?(\d+)$/, '$1').replace(/^0+/, '') || bill.bill_no;
    const isBill = bill.type === 'bill';
    const colCount       = isBill ? 8 : (isRunning ? 9 : 8);
    const typeLabel      = bill.type === 'bill' ? 'فاتورة' : bill.type === 'claim' ? 'مطالبة' : (bill.type || '—');
    const headerImgSrc   = headerImgB64;
    const watermarkSrc   = watermarkB64;

    const itemRows = (bill.items || []).map((item, i) => {
      const cQty  = parseFloat(item.contract_item?.quantity || 0);
      const tQty  = parseFloat(item.total_qty   || 0);
      const pQty  = getPreviouslyExecutedQtyFromItem(item);
      const curQty = getCurrentQtyFromItem(item);
      const rQty  = cQty - tQty;
      const pct   = item.percentage ?? (cQty > 0 ? Math.round((tQty / cQty) * 100) : 0);
      const price = parseFloat(item.contract_item?.unit_price || 0);
      const amt   = parseFloat(item.amount || 0);
      const desc  = escapeHtml(item.contract_item?.description_ar || item.contract_item?.description_en);
      const cells = isBill
        ? `<td>${i+1}</td><td class="desc">${desc}</td><td>${fmtQty(cQty)}</td><td>${fmtQty(pQty)}</td><td>${fmtQty(curQty)}</td><td>${fmtQty(tQty)}</td><td>${formatCurrency(price)}</td><td>${formatCurrency(amt)}</td>`
        : isRunning
        ? `<td>${i+1}</td><td class="desc">${desc}</td><td>${fmtQty(tQty)}</td><td>${fmtQty(cQty)}</td><td>${fmtQty(pQty)}</td><td>${fmtQty(rQty)}</td><td>${pct}%</td><td>${formatCurrency(price)}</td><td>${formatCurrency(amt)}</td>`
        : `<td>${i+1}</td><td class="desc">${desc}</td><td>${fmtQty(cQty)}</td><td>${fmtQty(pQty)}</td><td>${fmtQty(curQty)}</td><td>${fmtQty(tQty)}</td><td>${formatCurrency(price)}</td><td>${formatCurrency(amt)}</td>`;
      return `<tr>${cells}</tr>`;
    }).join('');

    const summaryRows = summary.map((row, si) =>
      `<tr class="sum${si === 0 ? ' sum-first' : ''}">
        <td colspan="${colCount - 1}" class="sum-lbl">${escapeHtml(row.label)}</td>
        <td class="sum-val">${formatCurrency(row.value)}</td>
      </tr>`
    ).join('');

    const ths = isBill
      ? `<th>رقم البند</th><th>بيان الأعمال</th><th>الكمية بالعقد</th><th>ما سبق تنفيذه</th><th>الكمية الحالية</th><th>إجمالي المنفذ</th><th>السعر</th><th>الإجمالي</th>`
      : isRunning
      ? `<th>رقم البند</th><th>بيان الاعمال</th><th>الكمية المنفذة</th><th>الكمية بالعقد</th><th>ما سبق تنفيذه</th><th>الكمية المتبقية</th><th>نسبة الاتمام</th><th>السعر</th><th>الإجمالي</th>`
      : `<th>رقم البند</th><th>بيان الاعمال</th><th>الكمية بالعقد</th><th>ما سبق تنفيذه</th><th>الكمية الحالية</th><th>اجمالي المنفذ</th><th>السعر</th><th>الإجمالي</th>`;

    const descW  = isRunning ? '26%' : '32%';
    const pgSize = isRunning ? 'A4 landscape' : 'A4 portrait';

    const html = `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
<meta charset="UTF-8"/>
<title>مستخلص أعمال رقم ${escapeHtml(bill.bill_no)}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Tahoma','Arial',sans-serif;direction:rtl;color:#000;background:#fff;font-size:12px;padding:20px 28px}
.watermark{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);width:70%;opacity:0.05;pointer-events:none;z-index:0}
.page-content{position:relative;z-index:1}
.header-img{width:100%;height:auto;border-bottom:4px double #000;padding-bottom:8px;margin-bottom:14px}
.bill-title{text-align:center;font-size:16px;font-weight:bold;color:#000080;margin-bottom:14px}
.info-row{display:flex;justify-content:space-between;margin-bottom:6px;font-size:12px}
.info-center{text-align:center;font-weight:bold;margin-bottom:12px;font-size:12px}
table{width:100%;border-collapse:collapse;font-size:11px}
th{background:#ddd;border:1px solid #000;padding:6px 4px;font-weight:bold;text-align:center;white-space:nowrap}
td{border:1px solid #000;padding:5px 4px;text-align:center;vertical-align:middle}
td.desc{text-align:right;padding-right:8px;max-width:${descW};word-wrap:break-word}
tr:nth-child(even) td{background:#f9f9f9}
tr.sum td{font-weight:bold;background:#f5f5f5!important}
tr.sum-first td{border-top:2px solid #000}
td.sum-lbl{text-align:center}
td.sum-val{text-align:center}
.amount-words{margin-top:18px;text-align:center;font-weight:bold;font-size:13px;color:#000080}
.signatures{display:flex;justify-content:space-between;margin-top:50px;text-align:center;font-size:12px}
.sig-item{flex:1;padding:0 15px}
.sig-title{font-weight:bold;margin-bottom:6px}
.sig-line{border-bottom:1px solid #000;margin-top:28px;margin-bottom:4px}
.sig-dots{color:#333;letter-spacing:1px}
@media print{body{padding:6px 10px}.watermark{position:fixed}@page{margin:.8cm;size:${pgSize}}}
</style>
</head>
<body>
<img src="${headerImgSrc}" class="header-img" alt=""/>
<img src="${watermarkSrc}" class="watermark" alt=""/>
<div class="page-content">
<div class="bill-title">مستخلص أعمال رقم ${escapeHtml(bill.bill_no)}</div>
<div class="info-row"><span><strong>التاريخ:</strong>&nbsp;${formatDate(bill.date)}</span><span><strong>نوع المستخلص:</strong>&nbsp;${escapeHtml(typeLabel)}</span></div>
<div class="info-row"><span><strong>اسم العميل:</strong>&nbsp;${customerTxt}</span><span><strong>فئة المستخلص:</strong>&nbsp;${escapeHtml(bill.type || '—')}</span></div>
<div class="info-row"><span><strong>اسم المشروع:</strong>&nbsp;${projectNameTxt}</span><span></span></div>
<div class="info-center">خلال الفترة من:&nbsp;${formatDate(bill.period_start)}&nbsp;&nbsp;&nbsp;إلى:&nbsp;${formatDate(bill.period_end)}</div>
<table>
<thead><tr>${ths}</tr></thead>
<tbody>${itemRows}${summaryRows}</tbody>
</table>
${netVal > 0 ? `<div class="amount-words">قيمة المستخلص فقط وقدره: ${convertNumberToArabicWords(netVal, t)} ${i18n.language === 'ar' ? t('progressBills.currency.egp') : 'EGP'}</div>` : ''}
<div class="signatures">
  <div class="sig-item"><div class="sig-title">توقيع الاعتماد</div><div class="sig-line"></div><div class="sig-dots">..............................</div></div>
  <div class="sig-item"><div class="sig-title">توقيع المراجعة</div><div class="sig-line"></div><div class="sig-dots">..............................</div></div>
  <div class="sig-item"><div class="sig-title">توقيع المحاسب</div><div class="sig-line"></div><div class="sig-dots">..............................</div></div>
</div>
</div>
<script>window.onload=function(){window.print()}</script>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (!win) { toast.error('يرجى السماح بالنوافذ المنبثقة لتفعيل الطباعة', { rtl: true }); return; }
    win.document.write(html);
    win.document.close();
  };

  const handleShowContract = async () => {
    try {
      const payload = await dispatch(getProgressBillDetails({ id, language: i18n.language })).unwrap();
      const details = payload?.data || payload;
      if (details?.contract) { setContractModal({ open: true, contract: details.contract }); return; }
    } catch { /* fall through */ }
    if (bill?.contract) { setContractModal({ open: true, contract: bill.contract }); return; }
    if (bill?.contract_id) {
      try {
        const cp = await dispatch(getContractsDetails(bill.contract_id)).unwrap();
        const cd = cp?.data || cp;
        if (cd) { setContractModal({ open: true, contract: cd }); }
        else toast.error(t('progressBills.errors.noContract'), { rtl: isRTL });
      } catch { toast.error(t('progressBills.errors.fetchContractData'), { rtl: isRTL }); }
    } else {
      toast.error(t('progressBills.errors.noContract'), { rtl: isRTL });
    }
  };

  if (isLoading) return <div style={pageStyle}><div style={cardStyle}>{t('progressBills.loading')}</div></div>;
  if (!bill)    return <div style={pageStyle}><div style={cardStyle}>{t('progressBills.notFound')}</div></div>;

  // ── derived values ──
  const docSummary   = patchSummary(Array.isArray(bill.invoice_summary) ? bill.invoice_summary : [], bill);
  const netRow       = docSummary.find((r) => r.key === 'net_amount');
  const netVal       = netRow ? parseFloat(netRow.value) || 0 : 0;
  const docIsRunning = bill.bill_category
    ? (bill.bill_category === 'running')
    : docSummary.length > 0
      ? !docSummary.some((r) => r.key === 'previous_payments')
      : bill.type !== 'bill'; // claim without summary = running-like
  const docSeqNo     = (bill.bill_no || '').replace(/^.*?(\d+)$/, '$1').replace(/^0+/, '') || bill.bill_no;
  const docTypeText  = bill.bill_category_label || (docIsRunning ? 'جاري' : 'ختامي');
  const docIsBill    = bill.type === 'bill';
  const docColCount  = docIsBill ? 8 : (docIsRunning ? 9 : 8);
  const docHeaders   = docIsBill
    ? ['رقم البند','بيان الأعمال','الكمية بالعقد','ما سبق تنفيذه','الكمية الحالية','إجمالي المنفذ','السعر','الإجمالي']
    : docIsRunning
    ? ['رقم البند','بيان الاعمال','الكمية المنفذة','الكمية بالعقد','ما سبق تنفيذه','الكمية المتبقية','نسبة الاتمام','السعر','الإجمالي']
    : ['رقم البند','بيان الاعمال','الكمية بالعقد','ما سبق تنفيذه','الكمية الحالية','اجمالي المنفذ','السعر','الإجمالي'];
  const dCell = { border:'1px solid #000', padding:'6px 5px', textAlign:'center', verticalAlign:'middle', color:'#000' };

  return (
    <div style={{ ...pageStyle, direction: dir, textAlign }}>
      <div style={cardStyle}>

        {/* Toolbar */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <div>
            <h2 style={{ margin:0, fontSize:18, color:'white' }}>{t('progressBills.viewDetails')}</h2>
            <div style={{ display:'flex', gap:16, marginTop:6, flexWrap:'wrap' }}>
              <span style={{ fontSize:13, color:'#aaa' }}>{bill.bill_no}</span>
              <span style={{ fontSize:13, color:'#aaa' }}>{getStatusLabel(bill.status)}</span>
              <span style={{ fontSize:13, color:'#aaa' }}>{formatDate(bill.date)}</span>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, flexShrink:0 }}>
            <button onClick={handlePrint} style={{ backgroundColor:'#AC2000', color:'white', border:'none', borderRadius:6, padding:'8px 14px', cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:14 }}>
              <FaPrint size={14} /> طباعة
            </button>
            <button onClick={() => navigate('/progress-bills')} style={{ backgroundColor:'transparent', color:'white', border:'1px solid #444', borderRadius:6, padding:'8px 12px', cursor:'pointer' }}>
              {t('progressBills.back')}
            </button>
          </div>
        </div>

        {/* ─── Document Preview (white background, matches print format) ─── */}
        <div style={{ backgroundColor:'#fff', borderRadius:8, padding:'24px 28px', marginBottom:16, direction:'rtl', fontFamily:"'Tahoma','Arial',sans-serif" }}>

          {/* Intro box */}
          <div style={{ border:'2px solid #000', padding:'12px 20px', textAlign:'center', fontWeight:'bold', fontSize:15, lineHeight:2, marginBottom:14, color:'#000' }}>
            بالإشارة إلى الموضوع عالية، يرجى التكرم بالموافقة على صرف الدفعة رقم ( {docSeqNo} ) {docTypeText} أعمال المذكورة أعلاه و كما هو موضح بالجدول أدناه .
          </div>

          {/* Info row */}
          <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 24px', marginBottom:12, fontSize:12, color:'#000' }}>
            <span><strong>المشروع:</strong> {bill.project?.name_ar || bill.project?.name_en || projectName}</span>
            <span><strong>العميل:</strong> {bill.project?.customer?.name_ar || bill.project?.customer?.name || '—'}</span>
            <span><strong>من:</strong> {formatDate(bill.period_start)}</span>
            <span><strong>إلى:</strong> {formatDate(bill.period_end)}</span>
          </div>

          {/* Table */}
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth: docIsRunning ? 920 : 740 }}>
              <thead>
                <tr>
                  {docHeaders.map((h, hi) => (
                    <th key={hi} style={{ background:'#ddd', border:'1px solid #000', padding:'7px 5px', fontWeight:'bold', textAlign:'center', fontSize:11, whiteSpace:'nowrap', color:'#000' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(bill.items || []).map((item, index) => {
                  const cQty   = parseFloat(item.contract_item?.quantity || 0);
                  const tQty   = parseFloat(item.total_qty   || 0);
                  const pQty   = getPreviouslyExecutedQtyFromItem(item);
                  const curQty = getCurrentQtyFromItem(item);
                  const rQty   = cQty - tQty;
                  const pct    = item.percentage ?? (cQty > 0 ? Math.round((tQty / cQty) * 100) : 0);
                  const price  = parseFloat(item.contract_item?.unit_price || 0);
                  const amt    = parseFloat(item.amount || 0);
                  return (
                    <tr key={item.id || index} style={{ background: index % 2 === 1 ? '#f9f9f9' : '#fff' }}>
                      <td style={dCell}>{index + 1}</td>
                      <td style={{ ...dCell, textAlign:'right', paddingRight:10, maxWidth: docIsRunning ? 200 : 240, wordWrap:'break-word' }}>
                        {item.contract_item?.description_ar || item.contract_item?.description_en || '—'}
                      </td>
                      {docIsBill ? (
                        <>
                          <td style={dCell}>{fmtQty(cQty)}</td>
                          <td style={dCell}>{fmtQty(pQty)}</td>
                          <td style={dCell}>{fmtQty(curQty)}</td>
                          <td style={dCell}>{fmtQty(tQty)}</td>
                          <td style={dCell}>{formatCurrency(price)}</td>
                          <td style={dCell}>{formatCurrency(amt)}</td>
                        </>
                      ) : docIsRunning ? (
                        <>
                          <td style={dCell}>{fmtQty(tQty)}</td>
                          <td style={dCell}>{fmtQty(cQty)}</td>
                          <td style={dCell}>{fmtQty(pQty)}</td>
                          <td style={dCell}>{fmtQty(rQty)}</td>
                          <td style={dCell}>{pct}%</td>
                          <td style={dCell}>{formatCurrency(price)}</td>
                          <td style={dCell}>{formatCurrency(amt)}</td>
                        </>
                      ) : (
                        <>
                          <td style={dCell}>{fmtQty(cQty)}</td>
                          <td style={dCell}>{fmtQty(pQty)}</td>
                          <td style={dCell}>{fmtQty(curQty)}</td>
                          <td style={dCell}>{fmtQty(tQty)}</td>
                          <td style={dCell}>{formatCurrency(price)}</td>
                          <td style={dCell}>{formatCurrency(amt)}</td>
                        </>
                      )}
                    </tr>
                  );
                })}

                {/* Summary rows */}
                {docSummary.map((row, si) => (
                  <tr key={row.key || si}>
                    <td colSpan={docColCount - 1} style={{ ...dCell, fontWeight:'bold', textAlign:'center', background:'#f5f5f5', borderTop: si === 0 ? '2px solid #000' : '1px solid #000' }}>
                      {row.label}
                    </td>
                    <td style={{ ...dCell, fontWeight:'bold', background:'#f5f5f5', borderTop: si === 0 ? '2px solid #000' : '1px solid #000' }}>
                      {formatCurrency(row.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Bill Value Text */}
          {netVal > 0 && (
            <div style={{ marginTop: '20px', marginBottom: '10px', direction: 'rtl', textAlign: 'center', fontSize: '14px', lineHeight: 1.8, color: '#000' }}>
              <div style={{ fontWeight: '700', color: '#000080' }}>
                {i18n.language === 'ar' ? 'قيمة المستخلص فقط وقدره: ' : 'Bill value only: '}
                {convertNumberToArabicWords(netVal, t)} {i18n.language === 'ar' ? t('progressBills.currency.egp') : 'EGP'}
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        {bill.notes && (
          <div style={{ marginBottom:16 }}>
            <DetailItem label={t('progressBills.viewFields.notes')} value={bill.notes} />
          </div>
        )}

        {/* Attachments */}
        {bill.attachments && Array.isArray(bill.attachments) && bill.attachments.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <h3 style={{ color:'white', marginBottom:12, fontSize:16 }}>{t('progressBills.viewFields.attachments')}</h3>
            <div style={{ display:'flex', flexWrap:'wrap', gap:12 }}>
              {bill.attachments.map((att, idx) => (
                <div key={att.id || idx} style={{ padding:'12px 16px', backgroundColor:'#1a1f2e', border:'1px solid #333', borderRadius:8, fontSize:14, color:'#ddd', display:'flex', alignItems:'center', gap:8 }}>
                  <span>{att.file_name || att.name || att.original_name || `${t('progressBills.viewFields.attachment')} ${idx+1}`}</span>
                  {att.file_path && (
                    <a href={att.file_path} target="_blank" rel="noopener noreferrer" style={{ color:'#4a9eff', textDecoration:'none', fontSize:12 }}>
                      ({t('progressBills.view')})
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contract */}
        {(bill.contract || bill.contract_id) && (
          <div style={{ marginBottom:16 }}>
            <h3 style={{ color:'white', marginBottom:12, fontSize:16 }}>{t('progressBills.contract')}</h3>
            <button onClick={handleShowContract} style={{ backgroundColor:'#6c757d', color:'white', border:'none', borderRadius:6, padding:'10px 16px', cursor:'pointer', fontSize:14 }}>
              {t('progressBills.viewContractDetails')}
            </button>
          </div>
        )}

        {/* Journal entry */}
        {bill.journal_entry && (
          <div style={{ marginBottom:16 }}>
            <h3 style={{ color:'white', marginBottom:12, fontSize:16 }}>{t('progressBills.journalEntries')}</h3>
            <button onClick={() => setJournalEntriesModal({ open: true, entry: bill.journal_entry })} style={{ backgroundColor:'#17a2b8', color:'white', border:'none', borderRadius:6, padding:'10px 16px', cursor:'pointer', fontSize:14 }}>
              {t('progressBills.viewDetails')}
            </button>
          </div>
        )}

      </div>

      {/* Journal Entries Modal */}
      {journalEntriesModal.open && journalEntriesModal.entry && ReactDOM.createPortal(
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:999999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={() => setJournalEntriesModal({ open:false, entry:null })}>
          <div style={{ ...modalCardStyle, maxWidth:900, maxHeight:'90vh', overflow:'auto', margin:'auto', direction:dir, textAlign }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop:0, marginBottom:16, color:'white' }}>{t('progressBills.journalEntriesModal.title')}</h3>
            {journalEntriesModal.entry ? (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(250px, 1fr))', gap:16, marginBottom:24, padding:16, backgroundColor:'#1a1f2e', borderRadius:8, border:'1px solid #333' }}>
                  <DetailItem label={t('progressBills.journalEntriesModal.fields.entryNo')}   value={journalEntriesModal.entry.id || '—'} />
                  <DetailItem label={t('progressBills.journalEntriesModal.fields.entryDate')} value={formatDate(journalEntriesModal.entry.entry_date)} />
                  <DetailItem label={t('progressBills.journalEntriesModal.fields.description')} value={journalEntriesModal.entry.description || '—'} />
                  <DetailItem label={t('progressBills.journalEntriesModal.fields.reference')} value={journalEntriesModal.entry.reference || '—'} />
                  <DetailItem label={t('progressBills.journalEntriesModal.fields.status')} value={journalEntriesModal.entry.status === 'posted' ? t('progressBills.journalEntriesModal.statusValues.posted') : journalEntriesModal.entry.status === 'draft' ? t('progressBills.journalEntriesModal.statusValues.draft') : journalEntriesModal.entry.status || '—'} />
                  <DetailItem label={t('progressBills.journalEntriesModal.fields.reversed')} value={journalEntriesModal.entry.is_reversed ? t('progressBills.journalEntriesModal.reversedValues.yes') : t('progressBills.journalEntriesModal.reversedValues.no')} />
                  <DetailItem label={t('progressBills.journalEntriesModal.fields.entryType')} value={journalEntriesModal.entry.entryable_type || '—'} />
                  <DetailItem label={t('progressBills.journalEntriesModal.fields.entryId')}   value={journalEntriesModal.entry.entryable_id || '—'} />
                  <DetailItem label={t('progressBills.journalEntriesModal.fields.createdAt')} value={formatDate(journalEntriesModal.entry.created_at)} />
                  <DetailItem label={t('progressBills.journalEntriesModal.fields.updatedAt')} value={formatDate(journalEntriesModal.entry.updated_at)} />
                </div>
                {journalEntriesModal.entry.details && Array.isArray(journalEntriesModal.entry.details) && journalEntriesModal.entry.details.length > 0 && (
                  <div style={{ backgroundColor:'#1a1f2e', border:'1px solid #333', borderRadius:12, overflow:'hidden' }}>
                    <h4 style={{ color:'white', marginBottom:12, padding:12, backgroundColor:'#2a2a2a', fontSize:14 }}>{t('progressBills.journalEntriesModal.detailsTitle')}</h4>
                    <div style={{ overflowX:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', minWidth:800 }}>
                        <thead>
                          <tr style={{ backgroundColor:'#AC2000' }}>
                            {[t('progressBills.journalEntriesModal.detailsHeaders.account'), t('progressBills.journalEntriesModal.detailsHeaders.code'), t('progressBills.journalEntriesModal.detailsHeaders.description'), t('progressBills.journalEntriesModal.detailsHeaders.debit'), t('progressBills.journalEntriesModal.detailsHeaders.credit')].map((h) => (
                              <th key={h} style={{ padding:14, textAlign:'center', color:'white', fontWeight:'bold', fontSize:14, borderBottom:'1px solid #333' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {journalEntriesModal.entry.details.map((detail, idx) => (
                            <tr key={detail.id || idx} style={{ borderBottom:'1px solid #333' }}>
                              <td style={cellStyle}>{detail.account?.name_ar || detail.account?.name_en || '—'}</td>
                              <td style={cellStyle}>{detail.account?.code || '—'}</td>
                              <td style={cellStyle}>{detail.line_description || '—'}</td>
                              <td style={cellStyle}>{formatCurrency(detail.debit)}</td>
                              <td style={cellStyle}>{formatCurrency(detail.credit)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign:'center', padding:40, color:'#ccc' }}>{t('progressBills.journalEntriesModal.noEntries')}</div>
            )}
            <div style={{ display:'flex', gap:12, justifyContent:'center', marginTop:24 }}>
              <button onClick={() => setJournalEntriesModal({ open:false, entry:null })} style={modalSecondaryButtonStyle}>
                {t('progressBills.journalEntriesModal.close')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Contract Modal */}
      {contractModal.open && contractModal.contract && ReactDOM.createPortal(
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:999999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={() => setContractModal({ open:false, contract:null })}>
          <div style={{ ...modalCardStyle, maxWidth:800, maxHeight:'90vh', overflow:'auto', margin:'auto', direction:dir, textAlign }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop:0, marginBottom:16, color:'white' }}>{t('progressBills.contractModal.title')}</h3>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(250px, 1fr))', gap:16, marginBottom:24 }}>
              <DetailItem label={t('progressBills.contractModal.fields.code')}       value={contractModal.contract.code || '—'} />
              <DetailItem label={t('progressBills.contractModal.fields.project')}    value={contractModal.contract.project?.name_ar || contractModal.contract.project?.name_en || '—'} />
              <DetailItem label={t('progressBills.contractModal.fields.contractor')} value={contractModal.contract.customer?.name_ar || contractModal.contract.customer?.name || '—'} />
              <DetailItem label={t('progressBills.contractModal.fields.quotation')}  value={contractModal.contract.quotation?.quotation_no || '—'} />
              <DetailItem label={t('progressBills.tableHeaders.date')}               value={formatDate(contractModal.contract.date)} />
              <DetailItem label={t('progressBills.contractModal.fields.startDate')}  value={formatDate(contractModal.contract.start_date)} />
              <DetailItem label={t('progressBills.contractModal.fields.endDate')}    value={formatDate(contractModal.contract.end_date)} />
              <DetailItem label={t('progressBills.tableHeaders.totalAmount')}        value={formatCurrency(contractModal.contract.total_amount)} />
              <DetailItem label={t('progressBills.contractModal.fields.retentionPercent')}     value={contractModal.contract.retention_percent ? `${contractModal.contract.retention_percent}%` : '—'} />
              <DetailItem label={t('progressBills.contractModal.fields.advancePaymentPercent')} value={contractModal.contract.advance_payment_percent ? `${contractModal.contract.advance_payment_percent}%` : '—'} />
              <DetailItem label={t('progressBills.contractModal.fields.status')} value={contractModal.contract.status || '—'} />
              <DetailItem label={t('progressBills.contractModal.fields.notes')}  value={contractModal.contract.notes || '—'} />
            </div>
            {contractModal.contract.attachments && Array.isArray(contractModal.contract.attachments) && contractModal.contract.attachments.length > 0 && (
              <div style={{ marginTop:16 }}>
                <h4 style={{ color:'white', marginBottom:12, fontSize:14 }}>{t('progressBills.contractModal.attachments')}</h4>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {contractModal.contract.attachments.map((att, idx) => (
                    <div key={att.id || idx} style={{ padding:'8px 12px', backgroundColor:'#1a1f2e', border:'1px solid #333', borderRadius:6, fontSize:12, color:'#ccc' }}>
                      {att.file_name || att.name || `${t('progressBills.contractModal.attachment')} ${idx+1}`}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display:'flex', gap:12, justifyContent:'center', marginTop:24 }}>
              <button onClick={() => setContractModal({ open:false, contract:null })} style={modalSecondaryButtonStyle}>
                {t('progressBills.contractModal.close')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const DetailItem = ({ label, value }) => (
  <div>
    <div style={{ fontSize:12, color:'#888', marginBottom:4 }}>{label}</div>
    <div style={{ fontSize:14, color:'white' }}>{value}</div>
  </div>
);

const pageStyle = {
  padding: 30,
  backgroundColor: '#14263b',
  minHeight: 'calc(100vh - 80px)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
};

const cardStyle = {
  width: '100%',
  maxWidth: 1200,
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
};

const cellStyle = {
  padding: 14,
  textAlign: 'center',
  color: '#ddd',
  fontSize: 13,
  borderBottom: '1px solid #333',
};

const modalCardStyle = {
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
  width: '100%',
  position: 'relative',
  zIndex: 20002,
  maxWidth: 420,
  margin: 'auto',
};

const modalSecondaryButtonStyle = {
  backgroundColor: '#666',
  color: 'white',
  border: 'none',
  padding: '12px 20px',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 14,
};

const convertNumberToArabicWords = (num, tFunc) => {
  const numStr = parseFloat(num).toFixed(2);
  const [integerPart, decimalPart] = numStr.split('.');
  let intNum = parseInt(integerPart, 10);

  const ones = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة'];
  const tens = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
  const teens = ['عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر', 'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر'];
  const hundreds = ['', 'مائة', 'مائتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة'];

  const convertThreeDigits = (n) => {
    if (n === 0) return '';
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) {
      const ten = Math.floor(n / 10);
      const one = n % 10;
      if (one === 0) return tens[ten];
      return `${ones[one]} و${tens[ten]}`;
    }
    const hundred = Math.floor(n / 100);
    const remainder = n % 100;
    if (remainder === 0) return hundreds[hundred];
    return `${hundreds[hundred]} و${convertThreeDigits(remainder)}`;
  };

  if (intNum === 0) return tFunc('progressBills.numberWords.zero', { defaultValue: 'صفر' });

  let result = '';
  if (intNum >= 1000000) {
    const millions = Math.floor(intNum / 1000000);
    result += convertThreeDigits(millions) + ' ' + tFunc('progressBills.numberWords.million', { defaultValue: 'مليون' });
    const remainder = intNum % 1000000;
    if (remainder > 0) result += ' ' + tFunc('progressBills.numberWords.and', { defaultValue: 'و' }) + ' ';
    intNum = remainder;
  }
  if (intNum >= 1000) {
    const thousands = Math.floor(intNum / 1000);
    if (thousands === 1) {
      result += tFunc('progressBills.numberWords.thousand', { defaultValue: 'ألف' });
    } else if (thousands === 2) {
      result += tFunc('progressBills.numberWords.twoThousands', { defaultValue: 'ألفين' });
    } else if (thousands >= 3 && thousands <= 10) {
      result += convertThreeDigits(thousands) + ' ' + tFunc('progressBills.numberWords.thousands', { defaultValue: 'آلاف' });
    } else {
      result += convertThreeDigits(thousands) + ' ' + tFunc('progressBills.numberWords.thousand', { defaultValue: 'ألف' });
    }
    const remainder = intNum % 1000;
    if (remainder > 0) result += ' ' + tFunc('progressBills.numberWords.and', { defaultValue: 'و' }) + ' ';
    intNum = remainder;
  }
  if (intNum > 0) {
    result += convertThreeDigits(intNum);
  }

  if (decimalPart && parseInt(decimalPart, 10) > 0) {
    result += ' ' + tFunc('progressBills.numberWords.and', { defaultValue: 'و' }) + ' ' + convertThreeDigits(parseInt(decimalPart, 10)) + ' ' + tFunc('progressBills.numberWords.qirsh', { defaultValue: 'قرش' });
  }

  return result || tFunc('progressBills.numberWords.zero', { defaultValue: 'صفر' });
};

export default ViewProgressBill;
